import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import { isNativeIapSupported } from "src/services/iap";
import { getProdFrontendFallbackUrl } from "src/config/frontendUrls";

interface UpgradeSubscriptionFreeProps {
  setIsPaymentOpen?: (open: boolean) => void;
  setInitialPaymentView?: (view: string) => void;
}

const UpgradeSubscriptionFree: React.FC<UpgradeSubscriptionFreeProps> = ({ setIsPaymentOpen, setInitialPaymentView }) => {
  // Check if running on native platform (iOS/Android)
  const isNativePlatform = Capacitor.isNativePlatform();
  const isHybridPlatform = isPlatform("hybrid");
  const isRunningNatively = isNativePlatform || isHybridPlatform;

  // Function to open web app in Capacitor Browser for native platforms
  const openWebAppInBrowser = async (path: string = "/home", queryParams: string = "") => {
    if (!isRunningNatively) {
      // On web, use normal navigation
      return false;
    }

    try {
      // Get base URL from environment variable
      const baseUrl = process.env.REACT_APP_FRONTEND_URI_WEB || getProdFrontendFallbackUrl();
      // Remove trailing slash and /home if present
      const cleanBaseUrl = baseUrl.replace(/\/$/, "").replace(/\/home$/, "");
      const fullUrl = `${cleanBaseUrl}${path}${queryParams ? `?${queryParams}` : ""}`;

      console.log("📱 UpgradeSubscriptionFree: Opening web app in Capacitor Browser:", fullUrl);

      // Open in Capacitor Browser
      await Browser.open({ url: fullUrl, presentationStyle: "popover" });
      return true;
    } catch (error) {
      console.error("UpgradeSubscriptionFree: Error opening Capacitor Browser:", error);
      return false;
    }
  };

  const handleUpgradeClick = async () => {
    // Native iOS/Android: open Manage Membership in-app (same as PaymentMethodMenu — no Capacitor Browser).
    if (isNativeIapSupported()) {
      if (setInitialPaymentView && setIsPaymentOpen) {
        setInitialPaymentView("manageMembership");
        setIsPaymentOpen(true);
        console.log("✅ UpgradeSubscriptionFree: Opened manageMembership drawer (native iOS/Android)");
      }
      return;
    }

    // Other native contexts (without in-app billing support): fallback to Capacitor Browser web flow.
    if (isRunningNatively) {
      // Pass query parameter to open payment drawer directly with manageMembership view
      const opened = await openWebAppInBrowser("/home", "openPayment=manageMembership&fromApp=true");
      if (opened) {
        console.log("✅ UpgradeSubscriptionFree: Opened Capacitor Browser with manageMembership drawer");
        return;
      }
    }

    // On web, open payment drawer directly
    if (setInitialPaymentView && setIsPaymentOpen) {
      setInitialPaymentView("manageMembership");
      setIsPaymentOpen(true);
      console.log("✅ UpgradeSubscriptionFree: Opened manageMembership drawer (web)");
    }
  };
  return (
    <Box
      sx={{
        width: "95%",
        bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
        borderRadius: 2,
        border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[70]}`,
        p: 1,
        mb: 1,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      {/* Text content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          //   gap: 0.5,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
            fontSize: "14px",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {/* Don't let an ad slow down your draft. */}
          Want more credits? Upgrade today.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
            fontSize: "14px",
            textAlign: "center",
            lineHeight: 1.2,
          }}
        >
          {/* Get instant, unlimited analysis all season. */}
        </Typography>
      </Box>

      {/* Upgrade button */}
      <Button
        variant="contained"
        onClick={handleUpgradeClick}
        sx={{
          width: "50%",
          bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
          color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
          borderRadius: 2,
          px: 2,
          py: 0,
          textTransform: "none",
          fontSize: "15px",
          fontWeight: "bold",
          "&:hover": {
            bgcolor: `${PureLightThemeCaaS.colors.alpha.orange[70]}`,
          },
        }}
      >
        Upgrade
      </Button>
    </Box>
  );
};

export default UpgradeSubscriptionFree;
